import { ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router, ActivatedRoute } from '@angular/router';

import { ApiEstimativaService } from 'src/app/modules/shared/services/api-estimativa.service';
import { DataService } from '../../services/data.service';
import { LanguageService } from '../../services/language.service';
import { TecnologiasService } from '../../services/tecnologias.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UsersManagementService } from 'src/app/modules/shared/services/usersManagement.service';
import { AuthService } from '../../services/auth.service';

import { Subject, throwError } from 'rxjs';
import { retryWhen, catchError, scan, delay, debounceTime, filter, take } from 'rxjs/operators';

import { trigger, transition, style, animate } from '@angular/animations';
import { InfoBarWithStatusService } from '../../services/info-bar-with-status.service';
import { RemoveDialogComponent } from 'src/app/modules/backoffice/remove-dialog/remove-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { EstimationState } from 'src/app/models/EstimationState';
import { PermissionService } from '../../services/permission.service';
import { Permission } from '../../security/permission';
import { DashboardFilterStateService } from '../../services/dashboard-filter-state.service';


interface tempEstimativa {
  name: string;
  tecnologia: string;
  data_i: string;
  data_f: string;
  project: string;
  state: EstimationState;
}

@Component({
  standalone: false,
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.css'],
  animations: [
    trigger('slideToggle', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in', style({ height: '0px', opacity: 0 })),
      ]),
    ]),
  ],
})
export class DataTableComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  selectedLabel: string = 'Lasts 1 month';
  sliderValue: number = 4;
  labels = ["1 month", "3 months", "6 months", "1 year", "All"];

  /** Columns displayed in the table. Columns IDs can be added, removed, or reordered. */
  @Input() displayedColumns: string[] = [];
  dataSource = new MatTableDataSource<tempEstimativa>();
  keysToKeep = ["name", "technology", "createdDate", "lastModifiedDate", "state", "project", "actions"];

  selectedStates: EstimationState[] = [];
  sessionDasID: any;

  innerWidth: any;
  smallScreen: boolean = false;

  variables: any;
  searchInput: string = '';
  paginatorStart: number = 0;
  paginatorPageSize: number = 5;
  paginatorTotalElements: number = 0;
  paginatorCurrentIndex: number = 0;
  filterDirection: string = '';
  searchText: string = '';
  monthsFilter: string = '';
  previousSortColumn: string | null = null;
  previousSortDirection: 'asc' | 'desc' | '' = '';
  showFiltersValue: boolean = true;
  isLoading: boolean = true;

  errorDetected: boolean = false;
  errorText: string = '';

  searchSubject = new Subject<string>();
  currentUserId: string = '';
  isSuperAdmin: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiEstimativaService: ApiEstimativaService,
    private service: DataService,
    private languageService: LanguageService,
    private _snackBar: MatSnackBar,
    private tecnologiasService: TecnologiasService,
    public dialog: MatDialog,
    private UsersManagementService: UsersManagementService,
    private infoBar: InfoBarWithStatusService,
    private cdRef: ChangeDetectorRef,
    private authService: AuthService,
    public permissionService: PermissionService,
    private dashboardFilterStateService: DashboardFilterStateService
  ) { }

  // Make EstimationState enum accessible in template
  public estimationState = EstimationState;

  public Permission = Permission;

  ngOnInit(): void {
    this.onResize();

    // Restore filters if they exist
    if (this.dashboardFilterStateService.hasFilters()) {
      const savedFilters = this.dashboardFilterStateService.getFilters();
      this.selectedStates = savedFilters.selectedStates;
      this.searchText = savedFilters.searchText;
      this.monthsFilter = savedFilters.monthsFilter;
      this.sliderValue = savedFilters.sliderValue;
      this.searchInput = savedFilters.searchInput;
    }

    this.languageService.translations$.subscribe((vars: any) => {
      this.variables = vars;
    });

    // Get current user ID for ownership checks
    this.authService.userDataLoaded$.pipe(
      filter(loaded => loaded),
      take(1)
    ).subscribe(() => {
      this.authService.getUserData().pipe(take(1)).subscribe(data => {
        if (data) {
          this.currentUserId = data.id;
        }
      });

      // Check if user is super admin
      this.authService.isSuperAdmin().pipe(take(1)).subscribe(isAdmin => {
        this.isSuperAdmin = isAdmin;
      });
    });

    this.sessionDasID = sessionStorage.getItem('session');

    this.searchSubject.pipe(debounceTime(1000)).subscribe((searchTerm) => {
      this.searchEstimates(searchTerm);
    });

    // Use restored filter values or defaults for initial load
    this.apiEstimativaService.searchEstimates(
      this.searchText,
      this.selectedStates,
      this.monthsFilter,
      this.filterDirection,
      this.paginatorStart,
      this.paginatorPageSize
    ).pipe(
      retryWhen(errors =>
        errors.pipe(
          scan((retryCount, error) => {
            if (retryCount >= 3) {
              throw error; // Stop retrying after 3 attempts
            }
            return retryCount + 1;
          }, 0),
          delay(2000) // Wait 2 seconds before retrying
        )
      ),
      catchError(err => {
        console.error('Error from searchEstimates Observable:', err);
        this.errorDetected = true;
        this.errorText = this.variables.detectedError;
        return throwError(() => err);
      })
    ).subscribe({
      next: (data) => {
        try {

          if (data) {
            this.dataSource.data = data.items;
            this.paginatorTotalElements = data.totalEstimates;
            this.paginator.length = this.paginatorTotalElements;
            this.paginatorCurrentIndex = 0;

            // Delay setting paginator length to allow change detection
            setTimeout(() => {
              if (this.paginator) {
                this.paginator.length = this.paginatorTotalElements;
                this.cdRef.detectChanges();
              }
            }, 100);
          }
        } catch (error) {
        }
      },
      error: (err) => {
        console.error('Error from observable:', err);
      },
      complete: () => {
        if (this.dataSource.data.length > 0) {
          this.isLoading = false
          this.errorDetected = false;
        }
        else {
          this.isLoading = false;
          this.errorDetected = false;
          this.errorText = '';
        }
      }
    });

    this.dataSource.filterPredicate = (data: any, filter: string) => {
      return data.idEstimate.trim().toLowerCase().includes(filter) ||
        data.name.trim().toLowerCase().includes(filter) ||
        data.catalog?.catalogName.trim().toLowerCase().includes(filter) ||
        data.createdDate.trim().toLowerCase().includes(filter) ||
        data.lastModifiedDate.trim().toLowerCase().includes(filter) ||
        data.project.name.trim().toLowerCase().includes(filter);
    };

  }

  // Listen to the size change of the window. For screen sizes < 450px it will only display 2 columns, for screen sizes <600px it will display 3 and for higher it will display all 5.
  @HostListener('window:resize')
  onResize() {
    this.innerWidth = window.innerWidth;

    if (this.innerWidth <= 450) {
      this.displayedColumns = ['estimativa', 'tecnologia'];
    } else if (this.innerWidth <= 600) {
      this.displayedColumns = ['estimativa', 'tecnologia', 'state'];
    } else {
      this.displayedColumns = ['ID', 'estimativa', 'tecnologia', 'data_i', 'data_f', 'state', 'project', 'actions'];
    }
  }

  // Initialize paginator and sort functionalities.
  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  onPageChange(event: PageEvent) {

    if (this.paginatorCurrentIndex < event.pageIndex) {
      this.isLoading = true;

      this.apiEstimativaService.searchEstimates(this.searchText, this.selectedStates, this.monthsFilter, this.filterDirection, event.pageIndex, event.pageSize).pipe(
        retryWhen(errors =>
          errors.pipe(
            scan((retryCount, error) => {
              if (retryCount >= 3) {
                throw error; // Stop retrying after 3 attempts
              }
              return retryCount + 1;
            }, 0),
            delay(2000) // Wait 2 seconds before retrying
          )
        ),
        catchError(err => {
          console.error('Error from searchEstimates Observable:', err);
          this.errorDetected = true;
          this.errorText = this.variables.detectedError;
          return throwError(() => err);
        })
      ).subscribe({
        next: (data) => {
          try {
            if (data) {
              this.dataSource.data = this.dataSource.data.concat(data.items);
              this.paginatorTotalElements = data.totalEstimates;
              this.paginatorCurrentIndex = event.pageIndex;

              // Delay setting paginator length to allow change detection
              setTimeout(() => {
                if (this.paginator) {
                  this.paginator.length = this.paginatorTotalElements;
                  this.paginator.pageIndex = event.pageIndex;
                  this.cdRef.detectChanges();  // Ensure UI updates
                }
              }, 100);
            }
          } catch (error) {
            // Handle any errors that occur during processing
            console.error('Error processing data:', error);
          }
        },
        error: (err) => {
          // Handle errors from the observable
          console.error('Error from observable:', err);
        },
        complete: () => {
          if (this.dataSource.data.length > 0) {
            this.isLoading = false;
            this.errorDetected = false;
          }
          else {
            this.isLoading = false;
            this.errorDetected = false;
            this.errorText = '';
          }
        }
      });
    }


  }


  showFilters() {
    this.showFiltersValue = !this.showFiltersValue;
  }


  isOwner(estimate: any): boolean {
    return estimate.createdBy === this.currentUserId;
  }

  //check button actions 
  checkEstimate(element: any) {
    if (this.canCheckEstimate(element)) {
      // Save current filters before navigation
      this.saveCurrentFilters();

      this.service.setEstimate(element);
      this.router.navigate(['/estimativa/resultado']);
    }
    else {
      return;
    }
  }

  canCheckEstimate(element: any) {
    return element.state !== this.estimationState.INCOMPLETE;
  }

  canCheckEstimateMessage(element: any) {
    if (element.state === this.estimationState.INCOMPLETE) {
      return this.variables.messageViewEstimateErrorIncomplete;
    }
    return this.variables.result;
  }

  //edit button actions
  canEditEstimate(estimate: any): boolean {
    return estimate.state === this.estimationState.COMPLETE || estimate.state === this.estimationState.INCOMPLETE || estimate.state === this.estimationState.TO_FIX;
  }

  canEditEstimateMessage(estimate: any): string {
    if (!this.canEditEstimate(estimate)) {
      return this.variables.messageEditEstimateErrorCertified;
    }
    else {
      return this.variables.edit;
    }
  }

  editEstimate(element: any) {
    if (this.canEditEstimate(element)) {
      // Save current filters before navigation
      this.saveCurrentFilters();

      this.service.setEstimativaNavigationContext({
        clickedItem: element
      });
      this.service.setEstimate(element.id);
      this.router.navigate(['/estimativa', { state: { estimateState: element.state } }]);
    }
    else {
      return;
    }
  }

  //delete button actions 
  deleteEstimate(element: any) {

    if (!this.canDeleteEstimate(element)) {
      return;
    }

    let dialogRef = this.dialog.open(RemoveDialogComponent);

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {

        this.apiEstimativaService.deleteEstimate(element.id).subscribe({
          next: (data) => {
            if (data.success) {
              this.infoBar.showSnackbar(this.variables.itemDeleted, ['successful-snackbar'], 3000);
              this.refreshAfterDelete();
            }
          },
          error: (error) => {
            console.error('Error:', error.error);
            this.infoBar.showSnackbar(this.variables.unknownError, ['error-snackbar'], 3000);
          }
        });
      }
    })
  }

  canDeleteEstimate(element: any): boolean {
    return element.state === this.estimationState.INCOMPLETE;
  }

  canDeleteEstimateMessage(element: any): string {
    if (!this.canDeleteEstimate(element)) {
      return this.variables.messageDeleteEstimateErrorNotIncomplete;
    }
    return this.variables.remove;
  }

  refreshAfterDelete(): void {
    this.isLoading = true;

    // Calculate the number of items currently shown on this page
    const currentPageItemCount = this.dataSource.data.length - (this.paginatorCurrentIndex * this.paginatorPageSize);

    // If we're deleting the last item on a page (and not on the first page), go back one page
    let targetPageIndex = this.paginatorCurrentIndex;
    if (currentPageItemCount <= 1 && this.paginatorCurrentIndex > 0) {
      targetPageIndex = this.paginatorCurrentIndex - 1;
    }

    this.apiEstimativaService.searchEstimates(
      this.searchText,
      this.selectedStates,
      this.monthsFilter,
      this.filterDirection,
      targetPageIndex,
      this.paginatorPageSize
    ).pipe(
      retryWhen(errors =>
        errors.pipe(
          scan((retryCount, error) => {
            if (retryCount >= 3) {
              throw error;
            }
            return retryCount + 1;
          }, 0),
          delay(2000)
        )
      ),
      catchError(err => {
        console.error('Error from searchEstimates Observable:', err);
        this.errorDetected = true;
        this.errorText = this.variables.detectedError;
        return throwError(() => err);
      })
    ).subscribe({
      next: (data) => {
        try {
          if (data) {
            // Reset the data array for the target page
            if (targetPageIndex === 0) {
              this.dataSource.data = data.items;
            } else {
              // Rebuild the data array up to and including the target page
              const itemsBeforeTargetPage = targetPageIndex * this.paginatorPageSize;
              this.dataSource.data = this.dataSource.data.slice(0, itemsBeforeTargetPage).concat(data.items);
            }

            this.paginatorTotalElements = data.totalEstimates;
            this.paginatorCurrentIndex = targetPageIndex;

            setTimeout(() => {
              if (this.paginator) {
                this.paginator.length = this.paginatorTotalElements;
                this.paginator.pageIndex = targetPageIndex;
                this.cdRef.detectChanges();
              }
            }, 100);
          }
        } catch (error) {
          console.error('Error processing data:', error);
        }
      },
      error: (err) => {
        console.error('Error from observable:', err);
      },
      complete: () => {
        this.isLoading = false;
        if (this.dataSource.data.length === 0) {
          this.errorDetected = false;
          this.errorText = '';
        } else {
          this.errorDetected = false;
        }
      }
    });
  }

  sortData(sort: Sort) {
    if (sort.direction) {
      this.isLoading = true;
      this.paginatorCurrentIndex = 0;
      this.filterDirection = this.getAscDescText(sort.active, sort.direction);

      this.apiEstimativaService.searchEstimates(this.searchText, this.selectedStates, this.monthsFilter, this.filterDirection, this.paginatorStart, this.paginatorPageSize).pipe(
        retryWhen(errors =>
          errors.pipe(
            scan((retryCount, error) => {
              if (retryCount >= 3) {
                throw error; // Stop retrying after 3 attempts
              }
              return retryCount + 1;
            }, 0),
            delay(2000) // Wait 2 seconds before retrying
          )
        ),
        catchError(err => {
          console.error('Error from searchEstimates Observable:', err);
          this.errorDetected = true;
          this.errorText = this.variables.detectedError;
          return throwError(() => err);
        })
      ).subscribe({
        next: (data) => {
          try {
            if (data) {
              this.dataSource.data = data.items;
              this.paginatorTotalElements = data.totalEstimates;  // Ensure this updates
              this.paginator.length = this.paginatorTotalElements; // Explicitly update length
              this.paginatorCurrentIndex = 0;

              // Delay setting paginator length to allow change detection
              setTimeout(() => {
                if (this.paginator) {
                  this.paginator.length = this.paginatorTotalElements;
                  this.cdRef.detectChanges();  // Ensure UI updates
                }
              }, 100);
            }
          } catch (error) {
            // Handle any errors that occur during processing
            console.error('Error processing data:', error);
          }
        },
        error: (err) => {
          // Handle errors from the observable
          console.error('Error from observable:', err);
        },
        complete: () => {
          if (this.dataSource.data.length > 0) {
            this.isLoading = false;
            this.errorDetected = false;
          }
          else {
            this.isLoading = false;
            this.errorDetected = false;
            this.errorText = '';
          }
        }
      });
    }
  }


  stringtoDate(b: string): Date {
    return new Date(parseInt(b[2]), parseInt(b[1]) - 1, parseInt(b[0]));
  }

  getStateText(level: string): string {
    switch (level) {
      case this.estimationState.COMPLETE:
        return this.variables.finished;
      case this.estimationState.INCOMPLETE:
        return this.variables.onGoing;
      case this.estimationState.CERTIFIED:
        return this.variables.certified;
      case this.estimationState.APPROVED:
        return this.variables.approved;
      case this.estimationState.REJECTED:
        return this.variables.rejected;
      case this.estimationState.TO_FIX:
        return this.variables.toFix;
      case this.estimationState.CLOSED:
        return this.variables.Closed;
      default:
        return '';
    }
  }

  getAscDescText(active: string, direction: string): string {
    switch (active) {
      case 'estimativa':
        return 'name' + direction;
      case 'tecnologia':
        return 'catalogname' + direction;
      case 'data_i':
        return 'creationdate' + direction;
      case 'data_f':
        return 'modifieddate' + direction;
      case 'state':
        return 'state' + direction;
      case 'project':
        return 'projectname' + direction;
    }
    return '';
  }

  compare(a: number | string | Date, b: number | string | Date, isAsc: boolean) {
    return (a > b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  onSearchChange(event: any) {
    const searchTerm = event.target.value;
    this.searchSubject.next(searchTerm);
  }

  searchEstimates(searchTerm: string) {

    this.isLoading = true;
    this.paginatorCurrentIndex = 0;
    this.searchText = searchTerm;

    this.apiEstimativaService.searchEstimates(searchTerm, this.selectedStates, this.monthsFilter, "", this.paginatorStart, this.paginatorPageSize).pipe(
      retryWhen(errors =>
        errors.pipe(
          scan((retryCount, error) => {
            if (retryCount >= 3) {
              throw error; // Stop retrying after 3 attempts
            }
            return retryCount + 1;
          }, 0),
          delay(2000) // Wait 2 seconds before retrying
        )
      ),
      catchError(err => {
        console.error('Error from searchEstimates Observable:', err);
        this.errorDetected = true;
        this.errorText = this.variables.detectedError;
        return throwError(() => err);
      })
    ).subscribe({
      next: (data) => {
        try {
          if (data) {
            this.dataSource.data = data.items;
            this.paginatorTotalElements = data.totalEstimates;  // Ensure this updates
            this.paginator.length = this.paginatorTotalElements; // Explicitly update length
            this.paginatorCurrentIndex = 0;

            // Delay setting paginator length to allow change detection
            setTimeout(() => {
              if (this.paginator) {
                this.paginator.length = this.paginatorTotalElements;
                this.cdRef.detectChanges();  // Ensure UI updates
              }
            }, 100);
          }
        } catch (error) {
          // Handle any errors that occur during processing
          console.error('Error processing data:', error);
        }
      },
      error: (err) => {
        // Handle errors from the observable
        console.error('Error from observable:', err);
      },
      complete: () => {
        if (this.dataSource.data.length > 0) {
          this.isLoading = false;
          this.errorDetected = false;
        }
        else {
          this.isLoading = false;
          this.errorDetected = false;
          this.errorText = '';
        }
      }
    });

  }


  //Method for date range
  updateLabel(event: any) {

    this.updateFilters();
  }

  //methods for state filter
  toggleState(state: EstimationState): void {
    if (this.selectedStates.includes(state)) {
      const index = this.selectedStates.indexOf(state);
      if (index !== -1) {
        this.selectedStates.splice(index, 1);
      };
    } else {
      this.selectedStates.push(state);
    }

    this.isLoading = true;
    this.paginatorCurrentIndex = 0;

    this.apiEstimativaService.searchEstimates(this.searchText, this.selectedStates, this.monthsFilter, this.filterDirection, this.paginatorStart, this.paginatorPageSize).pipe(
      retryWhen(errors =>
        errors.pipe(
          scan((retryCount, error) => {
            if (retryCount >= 3) {
              throw error; // Stop retrying after 3 attempts
            }
            return retryCount + 1;
          }, 0),
          delay(2000) // Wait 2 seconds before retrying
        )
      ),
      catchError(err => {
        console.error('Error from searchEstimates Observable:', err);
        this.errorDetected = true;
        this.errorText = this.variables.detectedError;
        return throwError(() => err);
      })
    ).subscribe({
      next: (data) => {
        try {
          if (data) {
            this.dataSource.data = data.items;
            this.paginatorTotalElements = data.totalEstimates;  // Ensure this updates
            this.paginator.length = this.paginatorTotalElements; // Explicitly update length
            this.paginatorCurrentIndex = 0;

            // Delay setting paginator length to allow change detection
            setTimeout(() => {
              if (this.paginator) {
                this.paginator.length = this.paginatorTotalElements;
                this.cdRef.detectChanges();  // Ensure UI updates
              }
            }, 100);
          }
        } catch (error) {
          // Handle any errors that occur during processing
          console.error('Error processing data:', error);
        }
      },
      error: (err) => {
        // Handle errors from the observable
        console.error('Error from observable:', err);
      },
      complete: () => {
        if (this.dataSource.data.length > 0) {
          this.isLoading = false;
          this.errorDetected = false;
        }
        else {
          this.isLoading = false;
          this.errorDetected = false;
          this.errorText = '';
        }
      }
    });
  }

  // Method to check if state is selected
  isStateSelected(state: EstimationState): boolean {
    return this.selectedStates.includes(state);
  }

  /**
   * Save current filter state before navigating away from dashboard
   */
  private saveCurrentFilters(): void {
    this.dashboardFilterStateService.saveFilters({
      selectedStates: this.selectedStates,
      searchText: this.searchText,
      monthsFilter: this.monthsFilter,
      sliderValue: this.sliderValue,
      searchInput: this.searchInput
    });
  }

  updateFilters(): void {

    let result = '';
    switch (this.sliderValue) {
      case 0:
        this.monthsFilter = '1';
        break;
      case 1:
        this.monthsFilter = '3';
        break;
      case 2:
        this.monthsFilter = '6';
        break;
      case 3:
        this.monthsFilter = '12';
        break;
      case 4:
        this.monthsFilter = '';
        break;
    }

    this.isLoading = !this.isLoading;
    this.paginatorCurrentIndex = 0;

    this.apiEstimativaService.searchEstimates(this.searchText, this.selectedStates, this.monthsFilter, this.filterDirection, this.paginatorStart, this.paginatorPageSize).pipe(
      retryWhen(errors =>
        errors.pipe(
          scan((retryCount, error) => {
            if (retryCount >= 3) {
              throw error; // Stop retrying after 3 attempts
            }
            return retryCount + 1;
          }, 0),
          delay(2000) // Wait 2 seconds before retrying
        )
      ),
      catchError(err => {
        console.error('Error from searchEstimates Observable:', err);
        this.errorDetected = true;
        this.errorText = this.variables.detectedError;
        return throwError(() => err);
      })
    ).subscribe({
      next: (data) => {
        try {
          if (data) {
            this.dataSource.data = data.items;
            this.paginatorTotalElements = data.totalEstimates;  // Ensure this updates
            this.paginator.length = this.paginatorTotalElements; // Explicitly update length
            this.paginatorCurrentIndex = 0;

            // Delay setting paginator length to allow change detection
            setTimeout(() => {
              if (this.paginator) {
                this.paginator.length = this.paginatorTotalElements;
                this.cdRef.detectChanges();  // Ensure UI updates
              }
            }, 100);
          }
        } catch (error) {
          // Handle any errors that occur during processing
          console.error('Error processing data:', error);
        }
      },
      error: (err) => {
        // Handle errors from the observable
        console.error('Error from observable:', err);
      },
      complete: () => {
        if (this.dataSource.data.length > 0) {
          this.isLoading = false;
          this.errorDetected = false;
        }
        else {
          this.isLoading = false;
          this.errorDetected = false;
          this.errorText = '';
        }
      }
    });
  }

}
